import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbstractDateComponent, ValueAccessorUtil } from '@railmybox/shared/util';
import { DatePicker } from 'primeng/datepicker';

dayjs.extend(utc);
dayjs.extend(timezone);

export interface DateTimeValue {
  startDate: dayjs.Dayjs;
  endDate: dayjs.Dayjs;
}

@UntilDestroy()
@Component({
  selector: 'eg-date-time',
  templateUrl: './date-time.component.html',
  styleUrls: ['./date-time.component.scss'],
  providers: [...ValueAccessorUtil.getValueAccessorProvider(DateTimeComponent)],
  standalone: false,
})
export class DateTimeComponent extends AbstractDateComponent<DateTimeValue | string> implements OnInit {
  private cdr = inject(ChangeDetectorRef);
  dateTime: Date;

  @ViewChild('dateTimePicker') dateTimePicker: DatePicker;
  @Input() timeInterval = 5;
  @Input() showTimePicker = true;

  get formattedValue(): string {
    const value = this.value as DateTimeValue;
    if (!value?.startDate) {
      return undefined;
    }
    return `${value.startDate.tz(dayjs.tz.guess(), true).format(this.localeFormat)} ${value.startDate
      .tz(dayjs.tz.guess(), true)
      .format('HH:mm')} `;
  }

  /**
   * emits selected interval
   */
  onInputChanged(event: Date): void {
    if (event) {
      this.checkInvalidDate(event);
    }

    if (!this.dateTime) {
      return;
    }

    const selected = dayjs(this.dateTime);
    const defaultDate = selected.isSame(dayjs(), 'day') && this.defaultMinDate ? dayjs(this.defaultDate) : selected.startOf('day');

    const values = {
      startDate: defaultDate.hour(this.dateTime.getHours()).minute(this.dateTime.getMinutes()).second(0),
      endDate: undefined,
    };

    this.value = values;
    const start = values.startDate.tz(dayjs.tz.guess(), true).toISOString();
    this.onChange(start);
    this.changed.emit(start);
    this.onTouched();
  }

  checkInvalidDate(event?: Date) {
    if (!this.showTimePicker) {
      const isOutOfRange = this.defaultDate <= this.minDateFormatted || this.defaultDate > this.maxDateFormatted;
      if (isOutOfRange) {
        if (this.value === undefined && event) {
          this.showTimePicker = true;
        } else if (this.value) {
          this.showTimePicker = true;
        } else {
          this.showTimePicker = false;
        }
      } else {
        this.showTimePicker = true;
      }

      this.cdr.detectChanges();
      this.dateTimePicker.updateUI();
      this.dateTimePicker.alignOverlay();
    }
  }

  openDatePicker(event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.control.disabled || this.disabled) {
      return;
    }
    this.checkInvalidDate();
    this.dateTimePicker.toggle();
  }

  override writeValue(value: string): void {
    if (!value) {
      super.writeValue(undefined);
      return;
    }
    const parsedDate = dayjs(value).tz(dayjs.tz.guess(), true);
    this.dateTime = dayjs(value).toDate();

    super.writeValue({
      startDate: parsedDate.isValid() ? parsedDate : undefined,
      endDate: parsedDate.isValid() ? parsedDate : undefined,
    });
  }

  override setDisabledState(isDisabled: boolean): void {
    super.setDisabledState(isDisabled);
    if (isDisabled === this.control.disabled) {
      return;
    }

    const disableFn = isDisabled ? 'disable' : 'enable';
    this.control[disableFn]();
  }
}
